import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, RefreshCw, Loader2, AlertTriangle, CheckCircle2, Layers, ListChecks } from "lucide-react";
import { ManualWeightDialog } from "@/components/ManualWeightDialog";
import { SelectedLabelsDialog } from "@/components/SelectedLabelsPrint";

// Print a stored label PDF through a hidden iframe (opens the browser print dialog).
export const toastLabelsReady = (lbl, what) => toast.success(`Labels ready — ${what}`, {
  description: `${lbl.total_labels || ""} CN22 label(s) auto-made (Horizontal · 4 per page) and saved in Generation History`,
  duration: 20000, action: { label: "Print", onClick: () => printLabelEntry(lbl.id).catch(() => toast.error("Print failed")) } });

export const printLabelEntry = async (entryId) => {
  const r = await axios.get(`${API}/bulk-label/history/${entryId}/download`,
    { headers: getAuthHeader(), responseType: "blob", timeout: 60000 });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  const fr = document.createElement("iframe");
  fr.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  fr.src = url;
  fr.onload = () => { try { fr.contentWindow.focus(); fr.contentWindow.print(); } catch { window.open(url, "_blank"); } };
  document.body.appendChild(fr);
  setTimeout(() => { fr.remove(); URL.revokeObjectURL(url); }, 120000);
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const CompanyPick = ({ row, companies, value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`h-7 text-xs w-44 ${value ? "" : "border-amber-400 bg-amber-50"}`} data-testid={`bl-ready-company-${row.id}`}>
      <SelectValue placeholder="Pick company…" />
    </SelectTrigger>
    <SelectContent>
      {companies.flatMap(c => {
        const id = c.id || c.name;
        const items = [<SelectItem key={id} value={id}>{c.name}</SelectItem>];
        if (c.name_hi?.trim()) items.push(<SelectItem key={`${id}:hi`} value={`${id}:hi`}>{c.name} (Hindi)</SelectItem>);
        return items;
      })}
    </SelectContent>
  </Select>
);

// Bulk Label — Bulk Data sheets from today + yesterday, one-click label print per row.
export const BulkLabelReadyTable = ({ companies, layout, labelsPerPage, mailingDate, convertUsdToInr, personalNote = true, onGenerated }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [picked, setPicked] = useState({});
  const [weightRow, setWeightRow] = useState(null);
  const [selectId, setSelectId] = useState(null);
  // Latest label already made for the row → pick from it; else make the labels first (same as Print).
  const selectPrint = (row) => {
    const latest = [...(row.labels || [])].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
    if (latest) setSelectId(latest.id); else generate(row, true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bulk-label/ready-from-bulk-data`, { headers: getAuthHeader() });
      setRows(r.data || []);
      // Announce auto-made labels once per browser.
      (r.data || []).forEach(row => (row.labels || []).filter(l => l.auto_generated).forEach(l => {
        const k = `auto-label-seen-${l.id}`;
        if (!localStorage.getItem(k)) { localStorage.setItem(k, "1"); toastLabelsReady(l, row.filename); }
      }));
    } catch { toast.error("Could not load Bulk Data sheets"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (row, thenSelect = false) => {
    const company = picked[row.id] ?? row.company_id;
    if (!company) { toast.error("Pick a sender company for this row first"); return; }
    setBusy(row.id);
    try {
      const r = await axios.post(`${API}/bulk-label/generate-from-bulk-data`, {
        bulk_data_id: row.id, sender_company_id: company, layout,
        labels_per_page: Number(labelsPerPage) || 4, mailing_date: mailingDate || null,
        convert_usd_to_inr: convertUsdToInr, personal_note: personalNote }, { headers: getAuthHeader(), timeout: 90000 });
      if (r.data.zero_weight_count > 0) toast.warning(`${r.data.zero_weight_count} parcel(s) printed with 0 g weight`);
      if (thenSelect) { toast.success(`Generated ${r.data.total_labels} labels — pick the ones to print`); setSelectId(r.data.id); }
      else { toast.success(`Generated ${r.data.total_labels} labels — opening print`); await printLabelEntry(r.data.id); }
      load(); onGenerated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Label generation failed"); }
    setBusy("");
  };

  const reprint = async (id) => {
    setBusy(id);
    try { await printLabelEntry(id); } catch { toast.error("Could not open the label PDF"); }
    setBusy("");
  };

  const todayIst = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const todayMissing = rows.filter(r => r.parcels - r.weighed > 0
    && new Date(r.created_at).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) === todayIst);
  const todayMissCount = todayMissing.reduce((a, r) => a + (r.parcels - r.weighed), 0);

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4" data-testid="bl-ready-table">
      {todayMissing.length > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="bl-weight-reminder">
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
          <div className="text-xs flex-1">
            <span className="font-semibold" data-testid="bl-weight-reminder-count">{todayMissCount} parcel(s)</span> in {todayMissing.length} of today's Bulk Data sheet(s) still have no weight — labels will print 0 g.
          </div>
          <Button size="sm" variant="outline" className="h-7 border-amber-400 text-amber-800 hover:bg-amber-100"
            onClick={() => setWeightRow(todayMissing[0])} data-testid="bl-weight-reminder-fix">Enter weights</Button>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-semibold text-slate-800 flex items-center gap-2"><Layers className="w-4 h-4 text-violet-600" /> Ready from Bulk Data <span className="text-xs font-normal text-slate-500">(today + yesterday)</span></div>
          <div className="text-[11px] text-slate-500">Uses the latest weights (scan / packing list / Database A). Layout: {layout} · {labelsPerPage} per page</div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="bl-ready-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center" data-testid="bl-ready-empty">{loading ? "Loading…" : "No Bulk Data sheets generated today or yesterday."}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="py-2 pr-3">Invoice</th><th className="pr-3">Bulk Data file</th><th className="pr-3">Parcels</th>
              <th className="pr-3">Weight</th><th className="pr-3">Company</th><th className="pr-3">Generated labels</th><th className="text-right">Print</th>
            </tr></thead>
            <tbody>
              {rows.map(row => {
                const miss = row.parcels - row.weighed;
                return (
                  <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`bl-ready-row-${row.id}`}>
                    <td className="py-2 pr-3 font-mono font-semibold text-violet-700" data-testid={`bl-ready-inv-${row.id}`}
                      title={row.invoices?.length ? `${row.invoice_from_sheet ? "From sheet" : "Matched from Order Register"}: ${row.invoices.join(", ")}` : ""}>
                      {row.invoice_no || "—"}{row.invoices?.length > 1 && <span className="ml-1 text-[10px] text-slate-500">+{row.invoices.length - 1}</span>}
                    </td>
                    <td className="pr-3 max-w-[220px]"><div className="truncate" title={row.filename}>{row.filename}</div><div className="text-[10px] text-slate-400">{fmt(row.created_at)} {row.generated_by_name && `· ${row.generated_by_name}`}</div></td>
                    <td className="pr-3">{row.parcels}</td>
                    <td className="pr-3" data-testid={`bl-ready-weight-${row.id}`}>
                      {miss > 0 ? (
                        <button type="button" onClick={() => setWeightRow(row)} data-testid={`bl-ready-weight-btn-${row.id}`}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 font-semibold hover:bg-amber-200 cursor-pointer"
                          title="Click to enter the missing weights">
                          <AlertTriangle className="w-3 h-3" /> {miss} without weight
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 font-semibold">
                          <CheckCircle2 className="w-3 h-3" /> {row.weighed}/{row.parcels} weighed
                        </span>
                      )}
                    </td>
                    <td className="pr-3"><CompanyPick row={row} companies={companies} value={picked[row.id] ?? row.company_id}
                      onChange={(v) => setPicked(p => ({ ...p, [row.id]: v }))} /></td>
                    <td className="pr-3">
                      <div className="flex flex-wrap gap-1">
                        {row.labels.length === 0 && <span className="text-slate-400">—</span>}
                        {row.labels.map(l => (
                          <button key={l.id} type="button" onClick={() => reprint(l.id)} disabled={!!busy}
                            title={`${l.filename} — click to print again`} data-testid={`bl-ready-reprint-${l.id}`}
                            className="inline-flex items-center gap-1 rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700 hover:bg-violet-100">
                            {busy === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Printer className="w-3 h-3" />}
                            {fmt(l.created_at)} · {l.total_labels} lbl · {l.labels_per_page}pp
                            {l.auto_generated && <span className="ml-0.5 rounded bg-emerald-600 px-1 text-[9px] font-bold text-white" data-testid={`bl-ready-auto-${l.id}`}>AUTO</span>}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="h-7 mr-1 border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => selectPrint(row)} disabled={!!busy}
                        title="Print only the parcels you tick" data-testid={`bl-ready-select-${row.id}`}>
                        <ListChecks className="w-3.5 h-3.5 mr-1" /> Selected
                      </Button>
                      <Button size="sm" className="h-7 bg-violet-600 hover:bg-violet-700" onClick={() => generate(row)} disabled={!!busy}
                        data-testid={`bl-ready-print-${row.id}`}>
                        {busy === row.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1" />} Print
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <SelectedLabelsDialog entryId={selectId} onClose={() => setSelectId(null)} />
      <ManualWeightDialog row={weightRow} onClose={() => setWeightRow(null)}
        onSaved={(andPrint) => { const r = weightRow; setWeightRow(null); if (andPrint) generate(r); else load(); }} />
    </div>
  );
};
